import { useCallback, useEffect, useMemo, useState } from "react";
import { Boxes, Building2, Clock3, DatabaseZap, RefreshCw, Search, SearchX } from "lucide-react";
import type { TenantListView } from "@ubisafe/shared";
import { api, ApiClientError } from "../lib/api";

function normalize(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
}

export function ClientsPage() {
  const [data, setData] = useState<TenantListView | null>(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await api.listClients());
    } catch (loadError) {
      setError(loadError instanceof ApiClientError ? loadError.message : "Não foi possível carregar os Clientes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const query = normalize(search.trim());
    if (!query) return data.tenants;
    return data.tenants.filter(
      (tenant) => normalize(tenant.name).includes(query) || normalize(tenant.identifier).includes(query),
    );
  }, [data, search]);

  return (
    <div className="page-stack clients-page">
      <section className="page-heading page-heading--split">
        <div>
          <span className="eyebrow">Carteira ativa</span>
          <h1>Clientes</h1>
          <p>Visão consolidada dos tenants usados no faturamento mensal.</p>
        </div>
        {data && (
          <div className="sync-summary" aria-label={`Última atualização em ${formatDateTime(data.synchronizedAt)}`}>
            <Clock3 aria-hidden="true" />
            <div>
              <span>Última atualização</span>
              <strong>{formatDateTime(data.synchronizedAt)}</strong>
            </div>
          </div>
        )}
      </section>

      {data?.source === "CONTROLLED" && (
        <div className="controlled-data-banner" role="note">
          <DatabaseZap aria-hidden="true" />
          <div>
            <strong>Dados controlados para validação</strong>
            <span>A fonte MySQL real substituirá este conjunto na versão 0.3.</span>
          </div>
        </div>
      )}

      <section className="tenant-toolbar" aria-label="Busca e resumo de Clientes">
        <label className="tenant-search">
          <Search aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por nome ou identificador"
            aria-label="Buscar Clientes por nome ou identificador"
          />
        </label>
        <div className="tenant-count">
          <Building2 aria-hidden="true" />
          <strong>{filtered.length}</strong>
          <span>{filtered.length === 1 ? "Cliente" : "Clientes"}</span>
        </div>
      </section>

      {loading && (
        <div className="tenant-grid" aria-label="Carregando Clientes" aria-busy="true">
          {Array.from({ length: 6 }, (_, index) => <div className="tenant-card tenant-card--skeleton" key={index} />)}
        </div>
      )}

      {!loading && error && (
        <div className="state-panel state-panel--error" role="alert">
          <RefreshCw aria-hidden="true" />
          <h2>Não foi possível carregar</h2>
          <p>{error}</p>
          <button className="button button--primary" onClick={() => void load()}>Tentar novamente</button>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="state-panel">
          <SearchX aria-hidden="true" />
          <h2>Nenhum Cliente encontrado</h2>
          <p>Revise o nome ou identificador informado.</p>
          <button className="button button--secondary" onClick={() => setSearch("")}>Limpar busca</button>
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="tenant-grid" aria-live="polite">
          {filtered.map((tenant) => (
            <article className="tenant-card tenant-card--client" key={tenant.identifier}>
              <div className="tenant-card__header">
                <div className="tenant-card__icon"><Building2 aria-hidden="true" /></div>
                <span className="tenant-card__type">Cliente</span>
              </div>
              <div className="tenant-card__identity">
                <h2>{tenant.name}</h2>
                <span>Identificador {tenant.identifier}</span>
              </div>
              <div className="tenant-card__metric">
                <Boxes aria-hidden="true" />
                <div>
                  <strong>{tenant.activePatrimonies.toLocaleString("pt-BR")}</strong>
                  <span>Patrimônios com contas ativas</span>
                </div>
              </div>
              <footer className="tenant-card__footer">
                <Clock3 aria-hidden="true" />
                Atualizado em {formatDateTime(tenant.synchronizedAt)}
              </footer>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
